import * as React from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface BulletListEditorProps {
  value: readonly string[];
  /** Called with the cleaned list (empty items dropped) whenever an item is committed, added or removed. */
  onChange: (value: string[]) => void;
  label: string;
  placeholder?: string;
  max?: number;
  readOnly?: boolean;
  className?: string;
  /** Persist on blur only (detail page) or on every keystroke (JD form). */
  mode?: 'blur' | 'live';
}

/**
 * Editable bullet list: one input per item, Enter adds the next item, Backspace on an empty item
 * removes it. Empty items are dropped when the list is committed.
 */
export function BulletListEditor({
  value,
  onChange,
  label,
  placeholder = 'New item',
  max,
  readOnly = false,
  className,
  mode = 'blur',
}: BulletListEditorProps) {
  const [items, setItems] = React.useState<string[]>(() => [...value]);
  const [focusIndex, setFocusIndex] = React.useState<number | null>(null);
  const inputs = React.useRef<Array<HTMLInputElement | null>>([]);
  const lastValue = React.useRef(value);

  // Reflect external changes (e.g. after a refetch) unless the user is mid-edit.
  React.useEffect(() => {
    if (lastValue.current !== value) {
      lastValue.current = value;
      if (focusIndex === null) setItems([...value]);
    }
  }, [value, focusIndex]);

  React.useEffect(() => {
    if (focusIndex !== null) {
      inputs.current[focusIndex]?.focus();
    }
  }, [focusIndex, items.length]);

  const clean = (list: string[]) => list.map((s) => s.trim()).filter(Boolean);
  const commit = (list: string[]) => {
    const next = clean(list);
    if (next.join('\u0000') !== clean([...value]).join('\u0000')) onChange(next);
  };

  const setItem = (i: number, text: string) => {
    const next = items.map((it, j) => (j === i ? text : it));
    setItems(next);
    if (mode === 'live') onChange(clean(next));
  };
  const remove = (i: number) => {
    const next = items.filter((_, j) => j !== i);
    setItems(next);
    setFocusIndex(Math.max(0, Math.min(i - 1, next.length - 1)));
    if (next.length === 0) setFocusIndex(null);
    commit(next);
  };
  const add = (at = items.length) => {
    if (max && items.length >= max) return;
    const next = [...items.slice(0, at), '', ...items.slice(at)];
    setItems(next);
    setFocusIndex(at);
  };

  if (readOnly) {
    return value.length === 0 ? (
      <p className={cn('text-sm text-muted-foreground', className)}>–</p>
    ) : (
      <ul className={cn('list-disc space-y-0.5 pl-5 text-sm', className)} aria-label={label}>
        {value.map((v, i) => (
          <li key={i}>{v}</li>
        ))}
      </ul>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)} role="group" aria-label={label}>
      <ol className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
            <Input
              ref={(el) => {
                inputs.current[i] = el;
              }}
              value={item}
              placeholder={placeholder}
              aria-label={`${label} ${i + 1}`}
              className="h-8"
              onChange={(e) => setItem(i, e.target.value)}
              onFocus={() => setFocusIndex(i)}
              onBlur={() => {
                setFocusIndex(null);
                if (mode === 'blur') commit(items);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (mode === 'blur') commit(items);
                  add(i + 1);
                } else if (e.key === 'Backspace' && item === '' && items.length > 0) {
                  e.preventDefault();
                  remove(i);
                } else if (e.key === 'Escape') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="h-8 w-8 shrink-0"
              aria-label={`Remove ${label} ${i + 1}`}
              onClick={() => remove(i)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ol>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => add()}
        disabled={Boolean(max && items.length >= max)}
      >
        <Plus aria-hidden="true" /> Add
      </Button>
    </div>
  );
}
